import { useEffect, useRef, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const SCRIPT_SRC = {
  turnstile: "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit",
  hcaptcha: "https://js.hcaptcha.com/1/api.js?render=explicit",
  recaptcha: "https://www.google.com/recaptcha/api.js?render=explicit",
};

function loadProviderScript(src) {
  const existing = document.querySelector(`script[src="${src}"]`);
  if (existing) {
    return new Promise((resolve, reject) => {
      if (window.turnstile || window.hcaptcha || window.grecaptcha) {
        resolve();
        return;
      }
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error(`Failed to load ${src}`)), { once: true });
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.body.appendChild(script);
  });
}

function removeWidget(provider, widgetId, host) {
  try {
    if (provider === "turnstile" && window.turnstile?.remove) {
      if (widgetId != null) window.turnstile.remove(widgetId);
      else if (host) window.turnstile.remove(host);
    } else if (provider === "hcaptcha" && window.hcaptcha?.remove && widgetId != null) {
      window.hcaptcha.remove(widgetId);
    } else if (provider === "recaptcha" && window.grecaptcha?.reset && widgetId != null) {
      window.grecaptcha.reset(widgetId);
    }
  } catch {
    // Widget may already have been torn down with the DOM.
  }
  if (host) host.innerHTML = "";
}

export function useBotProtection() {
  const [config, setConfig] = useState({ enabled: false, required: false, provider: null, site_key: null });

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${BACKEND_URL}/api/public/bot-protection`)
      .then((res) => {
        if (!cancelled) setConfig(res.data || {});
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  return config;
}

export default function BotProtection({ onToken, isDark }) {
  const config = useBotProtection();
  const hostRef = useRef(null);
  const widgetId = useRef(null);
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;

  const theme = isDark ? "dark" : "light";

  useEffect(() => {
    if (!config.enabled || !config.site_key || !config.provider) return undefined;

    let cancelled = false;
    const host = hostRef.current;
    const emit = (token) => onTokenRef.current?.(token);

    const renderIntoHost = () => {
      if (cancelled || !hostRef.current || widgetId.current != null) return;

      try {
        if (config.provider === "turnstile") {
          if (!window.turnstile) return;
          widgetId.current = window.turnstile.render(hostRef.current, {
            sitekey: config.site_key,
            callback: (token) => emit(token),
            "expired-callback": () => emit(""),
            theme,
          });
        } else if (config.provider === "hcaptcha") {
          if (!window.hcaptcha) return;
          widgetId.current = window.hcaptcha.render(hostRef.current, {
            sitekey: config.site_key,
            callback: (token) => emit(token),
            "expired-callback": () => emit(""),
            theme,
          });
        } else if (config.provider === "recaptcha") {
          if (!window.grecaptcha) return;
          window.grecaptcha.ready(() => {
            if (cancelled || !hostRef.current || widgetId.current != null) return;
            widgetId.current = window.grecaptcha.render(hostRef.current, {
              sitekey: config.site_key,
              callback: (token) => emit(token),
              "expired-callback": () => emit(""),
              theme,
            });
          });
        }
      } catch (err) {
        console.error("Bot protection widget failed to render", err);
        widgetId.current = null;
        removeWidget(config.provider, null, hostRef.current);
      }
    };

    const src = SCRIPT_SRC[config.provider];
    if (!src) return undefined;

    const alreadyReady =
      (config.provider === "turnstile" && window.turnstile) ||
      (config.provider === "hcaptcha" && window.hcaptcha) ||
      (config.provider === "recaptcha" && window.grecaptcha);

    if (alreadyReady) {
      renderIntoHost();
    } else {
      loadProviderScript(src)
        .then(() => {
          if (!cancelled) renderIntoHost();
        })
        .catch(() => {});
    }

    return () => {
      cancelled = true;
      const id = widgetId.current;
      widgetId.current = null;
      removeWidget(config.provider, id, host);
      emit("");
    };
  }, [config.enabled, config.provider, config.site_key, theme]);

  if (!config.enabled) {
    return null;
  }

  return (
    <div className="py-2" data-testid="bot-protection">
      <div ref={hostRef} />
    </div>
  );
}
